import '#global.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type AnyRoute, createRouter, type Router } from '@tanstack/react-router';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import minMax from 'dayjs/plugin/minMax';
import objectSupport from 'dayjs/plugin/objectSupport';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { enableMapSet } from 'immer';
import { MotionConfig } from 'motion/react';
import React, { Suspense } from 'react';
import reactDom from 'react-dom/client';

import { App } from '#app';
import { config as defaultConfig } from '#config.default';
import type { routeTree as coreRouteTree } from '#routeTree.gen';

import { DefaultError } from '#components/DefaultError';
import { LabeledLoader } from '#components/layout/LabeledLoader';
import { RootErrorBoundary } from '#components/layout/RootErrorBoundary';
import { AuthProvider } from '#contexts/AuthProvider';
import { CogniteClientProvider } from '#contexts/CogniteClientProvider';
import { ProjectProvider } from '#contexts/ProjectProvider';
import { env } from '#lib/env';
import type { ProjectConfigInput } from '#schema/ProjectConfigSchema';

export function init<RouteTreeType extends AnyRoute, const ConfigType extends ProjectConfigInput>(
  routeTree: RouteTreeType,
  projectConfig: ConfigType,
  AppContext: React.ComponentType<React.PropsWithChildren> = React.Fragment,
) {
  dayjs.extend(duration);
  dayjs.extend(minMax);
  dayjs.extend(objectSupport);
  dayjs.extend(timezone);
  dayjs.extend(utc);
  dayjs.tz.setDefault('UTC');

  enableMapSet();

  const router: Router<RouteTreeType, 'never', true> = createRouter({
    routeTree,
    context: {
      queryClient,
      cogniteClient: null,
    },
    defaultErrorComponent: DefaultError,
    defaultStructuralSharing: true,
    scrollRestoration: true,
  });

  const root = document.getElementById('root');

  if (root) {
    const RootElement = env.strictMode ? React.StrictMode : React.Fragment;
    reactDom.createRoot(root).render(
      <RootElement>
        <RootErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <ProjectProvider config={projectConfig ?? defaultConfig}>
              <Suspense fallback={<LabeledLoader label="Authenticating..." />}>
                <AuthProvider>
                  <CogniteClientProvider>
                    <MotionConfig transition={motionConfigTransition}>
                      <AppContext>
                        <App router={router} />
                      </AppContext>
                    </MotionConfig>
                  </CogniteClientProvider>
                </AuthProvider>
              </Suspense>
            </ProjectProvider>
          </QueryClientProvider>
        </RootErrorBoundary>
      </RootElement>,
    );
  }

  return router;
}

export type CoreRouter = Router<typeof coreRouteTree>;

declare module '@tanstack/react-router' {
  interface Register {
    router: CoreRouter;
  }

  interface StaticDataRouteOption {
    route?: {
      icon: React.FunctionComponent;
      sidebarPosition?: number;
      title: string;
    };
  }
}

const motionConfigTransition = {
  type: 'spring',
  duration: 0.5,
  bounce: 0.15,
} as const;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});
